import React, { useEffect } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CBadge,
  CImage,
  CListGroup,
  CListGroupItem,
} from "@coreui/react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import AppLoader from "../../components/seller/AppLoader";
import { fetchOrderDetails } from "../../features/seller/sellerSlice";
import { capitalizeFirst } from "../../utils/stringUtils";
import { timeAgo } from "../../utils/timeAgo";
import { formatCurrency } from "../../utils/price";

export default function SellerOrderDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    detail: order,
    loading,
    error,
  } = useSelector((state) => state.seller.orders);

  useEffect(() => {
    if (id) dispatch(fetchOrderDetails(id));
  }, [id, dispatch]);

  if (loading) return <AppLoader />;
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!order) return null;

  const {
    unique_order_id,
    created_at,
    status,
    payment_status,
    reverse_bid_id,
    total_price,
    total_price_with_tax,
    tax_amount,
    shipping_cost,
    admin_commission,
    seller_payout,
    payment_method,
    payment_reference,
    payment_released_at,
    payout_transferred_at,
    tracking_number,
    delivered_at,
    tpi_requested,
    tpi_status,
    hold_reason,
    cancel_reason,
    product,
    buyer,
  } = order;

  const isBidOrder = reverse_bid_id !== null;

  const show = (v, fallback = "N/A") =>
    v === null || v === undefined || v === "" ? fallback : v;

  // Status color maps
  const STATUS_BADGE_COLORS = {
    order: {
      approved: "success",
      pending: "warning",
      rejected: "danger",
      matched: "info",
      accepted: "info",
      shipped: "info",
      completed: "success",
      delivered: "success",
      default: "secondary",
    },
    payment: {
      pending: "danger",
      failed: "danger",
      paid: "success",
      completed: "success",
      refunded: "warning",
      default: "secondary",
    },
    source: {
      "bid order": "info",
      "direct order": "primary",
      default: "secondary",
    },
  };

  const getBadgeColor = (value, kind = "order") => {
    const map = STATUS_BADGE_COLORS[kind] || STATUS_BADGE_COLORS.order;
    if (!value) return map.default;
    const key = String(value).toLowerCase();
    return map[key] || map.default;
  };

  {
    /* compute once (optional) */
  }
  const orderBadge = getBadgeColor(status, "order");
  const paymentBadge = getBadgeColor(payment_status, "payment");
  const sourceLabel = isBidOrder ? "Bid Order" : "Direct Order";
  const sourceBadge = getBadgeColor(sourceLabel, "source");

  const ORDER_STATUS_LABELS = {
  pending: "Pending",
  accepted: "Accepted",
  matched: "Matched",
  shipped: "Shipped",
  delivered: "Delivered",
  completed: "Completed",
  rejected: "Rejected",

  admin_review: "Admin Review",
  awaiting_extra_payment: "Awaiting Extra Payment",
};


const getOrderStatusLabel = (status) => {
  if (!status) return "-";

  return (
    ORDER_STATUS_LABELS[status] ||
    status
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
};


  const formatAUD = (value) => {
    if (value === null || value === undefined || value === "") return "A$ 0";
    return `A$ ${Number(value).toLocaleString("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    })}`;
  };

  const TPI_STATUS_LABELS = {
    not_requested: "Not Requested",
    requested: "Requested",
    scheduled: "Scheduled",
    completed: "Completed",
    rejected: "Rejected",
  };

  const TPI_STATUS_COLORS = {
    not_requested: "secondary",
    requested: "info",
    scheduled: "warning",
    completed: "success",
    rejected: "danger",
  };

  const getTpiLabel = (status) => TPI_STATUS_LABELS[status] || "Unknown";

  const getTpiColor = (status) => TPI_STATUS_COLORS[status] || "secondary";

  return (
    <CRow className="g-4">
      {/* ================= ORDER HEADER ================= */}
      <CCol xs={12}>
        <CCard>
          <CCardBody className="d-flex justify-content-between flex-wrap gap-3">
            <div>
              <h4 className="mb-1">Order #{unique_order_id}</h4>
              <div className="text-muted">
                Created on: {timeAgo(created_at)}
              </div>
            </div>

            <div className="d-flex flex-column align-items-end gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">Order Status:</span>
                <CBadge color={orderBadge}>
                 {getOrderStatusLabel(status)}
                </CBadge>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">Payment:</span>
                <CBadge color={paymentBadge}>
                  {capitalizeFirst(show(payment_status))}
                </CBadge>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">Source:</span>
                <CBadge color={sourceBadge}>
                  {capitalizeFirst(show(sourceLabel))}
                </CBadge>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      {/* ================= BUYER INFO ================= */}
      <CCol md={6}>
        <CCard>
          <CCardHeader>Buyer Information</CCardHeader>
          <CListGroup flush>
            {/* ALWAYS VISIBLE */}
            <CListGroupItem>
              <strong>Name:</strong> {show(buyer?.name)}
            </CListGroupItem>

            {/* ADDRESS (OPTIONAL — KEEP IF REQUIRED) */}
            <CListGroupItem>
              <strong>Address:</strong>{" "}
              {show(
                `${buyer?.addressLine1 || ""} ${buyer?.addressLine2 || ""}, ${
                  buyer?.city || ""
                }, ${buyer?.state || ""} ${buyer?.postal_code || ""}`
              )}
            </CListGroupItem>

            {/* PAYMENT BASED VISIBILITY */}
            {payment_status === "pending" ? (
              <CListGroupItem className="text-muted fst-italic">
                <strong>
                  Note: Buyer contact details (company, email, phone) will be
                  visible once payment is completed.
                </strong>
              </CListGroupItem>
            ) : (
              <>
                <CListGroupItem>
                  <strong>Company:</strong> {show(buyer?.company_name)}
                </CListGroupItem>

                <CListGroupItem>
                  <strong>Email:</strong> {show(buyer?.email)}
                </CListGroupItem>

                <CListGroupItem>
                  <strong>Phone:</strong> {show(buyer?.phone)}
                </CListGroupItem>
              </>
            )}
          </CListGroup>
        </CCard>
      </CCol>

      {/* ================= PRODUCT INFO ================= */}
      <CCol md={6}>
        <CCard>
          <CCardHeader>Product Details</CCardHeader>
          <CListGroup flush>
            <CListGroupItem>
              <strong>Product:</strong> {product?.name}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Listing ID:</strong> {product?.listing_id}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Quantity:</strong> {product?.quantity}{" "}
              {product?.unit_of_measurement}
            </CListGroupItem>
            {console.log('Price per unit:',product?.price_per_unit)}
            <CListGroupItem>
              <strong>Price per Unit:</strong>{" "}
              {formatCurrency(product?.price_per_unit)}
              {/* {product?.price_per_unit
                ? `A$ ${product?.price_per_unit}`
                : "N/A"} */}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Product Status:</strong> {product?.status}
            </CListGroupItem>
          </CListGroup>
        </CCard>
      </CCol>

      {/* ================= PRODUCT SPECIFICATIONS ================= */}
      <CCol xs={12}>
        <CCard>
          <CCardHeader>Specifications & Compliance</CCardHeader>
          <CListGroup flush>
            <CListGroupItem>
              <strong>Sorting Type:</strong> {show(product?.sorting_type)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Attachment:</strong> {show(product?.attachment)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Waste Range:</strong>{" "}
              {product?.waste_range != null
                ? `${product.waste_range} %`
                : "N/A"}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Hazard Type:</strong>{" "}
              {product?.hazard_types?.includes("hazardous")
                ? "Hazardous"
                : "Non-Hazardous"}
            </CListGroupItem>
            {product?.hazard_description && (
              <CListGroupItem>
                <strong>Hazard Description:</strong>{" "}
                {product?.hazard_description}
              </CListGroupItem>
            )}
          </CListGroup>
        </CCard>
      </CCol>

      {/* ================= SHIPPING INFO ================= */}
      <CCol md={6}>
        <CCard>
          <CCardHeader>Shipping & Logistics</CCardHeader>
          <CListGroup flush>
            <CListGroupItem>
              <strong>Location:</strong> {product?.location}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Delivery Port:</strong> {show(product?.delivery_port)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Tracking Number:</strong> {show(tracking_number)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Shipping Cost:</strong> {show(shipping_cost)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Delivered At:</strong> {show(delivered_at)}
            </CListGroupItem>
          </CListGroup>
        </CCard>
      </CCol>

      {/* ================= PAYMENT & BILLING ================= */}
      <CCol md={6}>
        <CCard>
          <CCardHeader>Payment & Billing</CCardHeader>
          <CListGroup flush>
            <CListGroupItem>
              <strong>Subtotal:</strong> {formatCurrency(total_price)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Tax:</strong> {formatCurrency(tax_amount)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Total (With Tax):</strong>{" "}
              {formatCurrency(total_price_with_tax)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Admin Commission:</strong> {formatCurrency(admin_commission)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Seller Payout:</strong>{" "}
              <strong>{formatCurrency(seller_payout)}</strong>
            </CListGroupItem>
            <CListGroupItem>
              <strong>Payment Method:</strong> {show(payment_method)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Payment Reference:</strong> {show(payment_reference)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Payment Released:</strong> {show(payment_released_at)}
            </CListGroupItem>
            <CListGroupItem>
              <strong>Payout Transferred:</strong> {show(payout_transferred_at)}
            </CListGroupItem>
          </CListGroup>
        </CCard>
      </CCol>

      {/* ================= BID INFO ================= */}
      {product?.allow_bidding === 1 && (
        <CCol xs={12}>
          <CCard>
            <CCardHeader>Bid / Auction Information</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Auction Status:</strong> {product?.auction_status}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Minimum Asking Price:</strong>{" "}
                {formatCurrency(product?.minimum_asking_price)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Last Bid Price:</strong>{" "}
                {formatCurrency(product?.last_bid_price)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Bid Increment:</strong>{" "}
                {formatCurrency(product?.bid_increment)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Bid Closed At:</strong> {product?.bid_closed_at}
              </CListGroupItem>
            </CListGroup>
          </CCard>
        </CCol>
      )}

      {/* ================= INSPECTION / ADMIN ================= */}
      <CCol xs={12}>
        <CCard>
          <CCardHeader>Inspection & Admin</CCardHeader>
          <CListGroup flush>
            <CListGroupItem>
              <strong>TPI Requested:</strong> {tpi_requested ? "Yes" : "No"}
            </CListGroupItem>
            <CListGroupItem>
              <strong>TPI Status:</strong>{" "}
              <CBadge color={getTpiColor(tpi_status)}>
                {getTpiLabel(tpi_status)}
              </CBadge>
            </CListGroupItem>
            {hold_reason && (
              <CListGroupItem>
                <strong>Hold Reason:</strong> {hold_reason}
              </CListGroupItem>
            )}
            {cancel_reason && (
              <CListGroupItem>
                <strong>Cancel Reason:</strong> {cancel_reason}
              </CListGroupItem>
            )}
          </CListGroup>
        </CCard>
      </CCol>
    </CRow>
  );
}
